/**
 * Admin — learn.4klabs.ai catalog curation
 *
 * Attach an already-published video (by share token or conversation id) to the
 * public catalog with subject / grade / unit / order metadata. Re-submitting the
 * same share token updates its metadata (idempotent upsert). Curating a video also
 * enables precise review mode (?review=true) on its share link.
 */

import { createFileRoute } from '@tanstack/react-router';
import { Fragment, useEffect, useState } from 'react';
import { RefreshCw, Trash2, Plus, Star, StarOff, Eye, EyeOff, MessageSquare, ChevronDown } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { CourseBuilder } from '../../components/learn/CourseBuilder';
import {
  adminListExperiences, adminCreateExperience, adminUpdateExperience, adminDeleteExperience,
  adminGetExperienceFeedback, type CatalogItem, type ExperienceFeedbackData,
} from '../../services/learnAPI';

export const Route = createFileRoute('/admin/learn')({
  component: AdminLearn,
});

const SUBJECT_SUGGESTIONS = ['Mathematics', 'Physics', 'Chemistry', 'Biology', 'Science', 'Excel'];
const REACTION_LABELS: Record<string, string> = {
  clear: 'Clear & easy to follow',
  'made-me-think': 'Made me think',
  'loved-interaction': 'Loved the interaction',
  'too-fast': 'Too fast',
  'want-more': 'Want more like this',
};

function AdminLearn() {
  const [items, setItems] = useState<CatalogItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Per-experience feedback drilldown
  const [openId, setOpenId] = useState<string | null>(null);
  const [fb, setFb] = useState<ExperienceFeedbackData | null>(null);
  const [fbLoading, setFbLoading] = useState(false);

  async function toggleFeedback(id: string) {
    if (openId === id) { setOpenId(null); setFb(null); return; }
    setOpenId(id); setFb(null); setFbLoading(true);
    try {
      setFb(await adminGetExperienceFeedback(id));
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load feedback');
    } finally {
      setFbLoading(false);
    }
  }

  // Add/curate form
  const [shareToken, setShareToken] = useState('');
  const [conversationId, setConversationId] = useState('');
  const [subject, setSubject] = useState('');
  const [grade, setGrade] = useState('');
  const [unit, setUnit] = useState('');
  const [order, setOrder] = useState('0');
  const [featured, setFeatured] = useState(false);

  async function load() {
    setLoading(true);
    try {
      setItems(await adminListExperiences());
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load catalog');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  async function handleAdd() {
    setError(null);
    if (!shareToken.trim() && !conversationId.trim()) {
      setError('Enter a share token or conversation id.');
      return;
    }
    setBusy(true);
    try {
      await adminCreateExperience({
        shareToken: shareToken.trim() || undefined,
        conversationId: conversationId.trim() || undefined,
        subject: subject.trim() || null,
        grade: grade.trim() || null,
        unit: unit.trim() || null,
        order: Number(order) || 0,
        featured,
      });
      setShareToken(''); setConversationId(''); setSubject(''); setGrade(''); setUnit(''); setOrder('0'); setFeatured(false);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to add to catalog');
    } finally {
      setBusy(false);
    }
  }

  async function patch(item: CatalogItem, p: Partial<Pick<CatalogItem, 'featured' | 'isPublished'>>) {
    try {
      await adminUpdateExperience(item.id, p);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to update');
    }
  }

  async function remove(item: CatalogItem) {
    if (!confirm(`Remove "${item.title ?? item.shareToken}" from the catalog? (The video itself is not deleted.)`)) return;
    try {
      await adminDeleteExperience(item.id);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to remove');
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Learn Catalog</h1>
          <p className="text-muted-foreground">
            Curate published videos into the public learn.4klabs.ai catalog. Publish a video first, then add its share token here.
          </p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading}>
          <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {error && (
        <div className="rounded border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">{error}</div>
      )}

      {/* Add */}
      <Card>
        <CardHeader><CardTitle>Add to catalog</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
              <Label>Share token</Label>
              <Input value={shareToken} onChange={(e) => setShareToken(e.target.value)} placeholder="e.g. DlzBD453QRTZB3uwDt2RQg" />
            </div>
            <div>
              <Label>…or Conversation ID</Label>
              <Input value={conversationId} onChange={(e) => setConversationId(e.target.value)} placeholder="uses the most recent active share link" />
            </div>
            <div>
              <Label>Subject</Label>
              <Input list="learn-subjects" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Physics" />
              <datalist id="learn-subjects">{SUBJECT_SUGGESTIONS.map((s) => <option key={s} value={s} />)}</datalist>
            </div>
            <div>
              <Label>Grade</Label>
              <Input value={grade} onChange={(e) => setGrade(e.target.value)} placeholder="Class 10" />
            </div>
            <div>
              <Label>Unit</Label>
              <Input value={unit} onChange={(e) => setUnit(e.target.value)} placeholder="Waves & Motion" />
            </div>
            <div className="flex items-end gap-4">
              <div className="w-24">
                <Label>Order</Label>
                <Input type="number" value={order} onChange={(e) => setOrder(e.target.value)} />
              </div>
              <label className="flex items-center gap-2 pb-2 text-sm font-medium">
                <input type="checkbox" checked={featured} onChange={(e) => setFeatured(e.target.checked)} className="h-4 w-4" />
                Featured
              </label>
            </div>
          </div>
          <div className="mt-4">
            <Button onClick={handleAdd} disabled={busy}>
              <Plus className="mr-1 h-4 w-4" /> Add / update
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* List */}
      <Card>
        <CardHeader><CardTitle>Catalog ({items.length})</CardTitle></CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex min-h-[160px] items-center justify-center">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Subject</TableHead>
                  <TableHead>Grade</TableHead>
                  <TableHead>Unit</TableHead>
                  <TableHead className="text-center">Order</TableHead>
                  <TableHead className="text-center">Ratings</TableHead>
                  <TableHead className="text-center">State</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((it) => (
                  <Fragment key={it.id}>
                  <TableRow>
                    <TableCell className="max-w-[260px]">
                      <div className="truncate font-medium">{it.title ?? '—'}</div>
                      <div className="truncate text-xs text-muted-foreground">{it.shareToken}</div>
                    </TableCell>
                    <TableCell>{it.subject ?? '—'}</TableCell>
                    <TableCell>{it.grade ?? '—'}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{it.unit ?? '—'}</TableCell>
                    <TableCell className="text-center">{it.order}</TableCell>
                    <TableCell className="text-center">
                      <Button variant="ghost" size="sm" className="h-7 gap-1.5 px-2 text-xs" title="View feedback" onClick={() => toggleFeedback(it.id)}>
                        <MessageSquare className="h-3.5 w-3.5" />
                        {it.ratingCount > 0 ? `${it.averageRating?.toFixed(1)}★ · ${it.ratingCount}` : '0'}
                        <ChevronDown className={`h-3 w-3 transition-transform ${openId === it.id ? 'rotate-180' : ''}`} />
                      </Button>
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Badge variant={it.isPublished ? 'default' : 'secondary'}>{it.isPublished ? 'live' : 'hidden'}</Badge>
                        {it.featured && <Badge variant="outline">featured</Badge>}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" title={it.featured ? 'Unfeature' : 'Feature'} onClick={() => patch(it, { featured: !it.featured })}>
                        {it.featured ? <StarOff className="h-4 w-4" /> : <Star className="h-4 w-4" />}
                      </Button>
                      <Button variant="ghost" size="sm" title={it.isPublished ? 'Hide' : 'Publish'} onClick={() => patch(it, { isPublished: !it.isPublished })}>
                        {it.isPublished ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                      <Button variant="ghost" size="sm" title="Remove" onClick={() => remove(it)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                  {openId === it.id && (
                    <TableRow>
                      <TableCell colSpan={8} className="bg-muted/40 p-0">
                        <FeedbackPanel loading={fbLoading} data={fb} />
                      </TableCell>
                    </TableRow>
                  )}
                  </Fragment>
                ))}
                {items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} className="py-8 text-center text-muted-foreground">
                      No catalog entries yet. Add one above.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      <CourseBuilder />
    </div>
  );
}

function FeedbackPanel({ loading, data }: { loading: boolean; data: ExperienceFeedbackData | null }) {
  if (loading) return <div className="p-6 text-sm text-muted-foreground">Loading feedback…</div>;
  if (!data) return null;
  const { summary, items } = data;
  if (summary.count === 0) return <div className="p-6 text-sm text-muted-foreground">No feedback submitted yet for this experience.</div>;
  return (
    <div className="space-y-4 p-5">
      <div className="flex flex-wrap items-center gap-4">
        <div className="text-sm">
          <span className="font-semibold">{summary.averageRating?.toFixed(1)}★</span>{' '}
          <span className="text-muted-foreground">avg · {summary.count} {summary.count === 1 ? 'response' : 'responses'}</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {Object.entries(summary.reactions).sort((a, b) => b[1] - a[1]).map(([k, n]) => (
            <Badge key={k} variant="secondary">{REACTION_LABELS[k] ?? k} · {n}</Badge>
          ))}
        </div>
      </div>
      <div className="space-y-2">
        {items.map((f) => (
          <div key={f.id} className="rounded-lg border bg-background p-3">
            <div className="flex items-center justify-between">
              <div className="text-sm text-amber-500">
                {'★'.repeat(f.rating)}<span className="text-muted-foreground/30">{'★'.repeat(5 - f.rating)}</span>
              </div>
              <div className="text-xs text-muted-foreground">{new Date(f.submittedAt).toLocaleString()}</div>
            </div>
            {f.reactions.length > 0 && (
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {f.reactions.map((r) => (
                  <span key={r} className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium">{REACTION_LABELS[r] ?? r}</span>
                ))}
              </div>
            )}
            {f.comment && <p className="mt-2 whitespace-pre-wrap text-sm">{f.comment}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}
